import React, { useEffect, useState } from 'react';
import './flightTerms.css';
import usePageMeta from "../../../hooks/usePageMeta";

/* ============================================================
   Flight Booking Terms & Conditions
   ============================================================ */

const flightTerms = [
  {
    number: 1,
    title: 'Flight Booking',
    paragraphs: [
      'MakeBeeTrip is a B2C travel platform that facilitates flight bookings through airlines, airline distribution systems, technology providers and other authorized travel suppliers.',
      'Flight bookings are subject to the availability, fare rules, terms and conditions of the respective airline.',
    ],
  },

  {
    number: 2,
    title: 'Flight Availability and Fares',
    paragraphs: [
      'All flight fares, seats and availability displayed on the website are subject to availability.',
      'Airline fares may change at any time before the booking is completed.',
      'The displayed fare may change due to:',
    ],
    bullets: [
      'Airline inventory changes',
      'Seat availability',
      'Fare expiry',
      'Airline fare revisions',
      'Taxes and government charges',
      'Currency fluctuations',
      'Passenger or itinerary changes',
    ],
    after: [
      'The final applicable fare will be confirmed before payment or ticket issuance, as applicable.',
    ],
  },

  {
    number: 3,
    title: 'Booking Confirmation',
    paragraphs: [
      'A flight booking is considered confirmed only after the applicable airline booking has been successfully confirmed and the ticket has been issued.',
    ],
    emphasis:
      'Payment confirmation alone does not necessarily mean that a flight ticket has been issued.',
    after: [
      'Customers should wait for the e-ticket, PNR or booking confirmation before considering the booking confirmed.',
    ],
  },

  {
    number: 4,
    title: 'Passenger Information',
    paragraphs: [
      'Customers must provide accurate and complete passenger information while making a booking.',
      'This may include:',
    ],
    bullets: [
      'Full name',
      'Date of birth',
      'Gender',
      'Nationality',
      'Passport details for international travel',
      'Contact details',
      'Other information required by the airline',
    ],
    after: [
      'Passenger names and other details should be entered carefully according to the applicable travel documents and airline requirements.',
      'Incorrect information may result in additional charges, correction restrictions or cancellation.',
    ],
  },

  {
    number: 5,
    title: 'Name Correction and Name Change',
    paragraphs: [
      "Name correction or name change is subject to the applicable airline's rules.",
      'Some airlines may permit limited corrections, while others may not permit name changes after ticket issuance.',
      'Any applicable airline charges, fare difference and MakeBeeTrip service charges may apply.',
    ],
  },

  {
    number: 6,
    title: 'Passport and Visa Requirements',
    paragraphs: [
      'For international travel, passengers are responsible for having all required travel documents, including:',
    ],
    bullets: [
      'Valid passport',
      'Appropriate visa',
      'Transit visa, where applicable',
      'Required permits',
      'Other destination-specific documents',
    ],
    after: [
      'MakeBeeTrip does not guarantee visa approval, immigration clearance or entry into any country.',
      'Passengers should verify the latest entry and transit requirements before travelling.',
    ],
  },

  {
    number: 7,
    title: 'Flight Schedule Changes',
    paragraphs: [
      'Airlines may change:',
    ],
    bullets: [
      'Departure time',
      'Arrival time',
      'Flight number',
      'Aircraft',
      'Route',
      'Terminal',
      'Other operational details',
    ],
    after: [
      'MakeBeeTrip may communicate schedule changes received from the airline or relevant supplier.',
      'Passengers should check their flight status before departure.',
    ],
  },

  {
    number: 8,
    title: 'Flight Delays and Cancellations by Airline',
    paragraphs: [
      'Flight delays, cancellations, diversions and other operational disruptions are controlled by the respective airline.',
      'If an airline cancels or materially changes a flight, MakeBeeTrip will assist the customer with the options made available by the airline.',
      "Depending on the airline's policy, options may include:",
    ],
    bullets: [
      'Rebooking',
      'Rescheduling',
      'Alternative flight',
      'Credit or voucher',
      'Refund',
    ],
    after: [
      "The available option will depend on the airline's applicable policy and fare conditions.",
    ],
  },

  {
    number: 9,
    title: 'Passenger-Initiated Cancellation',
    paragraphs: [
      "Passenger-initiated cancellation is subject to the applicable airline's fare rules.",
      'Cancellation charges may include:',
    ],
    bullets: [
      'Airline cancellation penalty',
      'Non-refundable fare components',
      'Applicable taxes or charges',
      'MakeBeeTrip service charges',
      'Other applicable supplier charges',
    ],
    after: [
      'Certain tickets may be completely non-refundable.',
    ],
  },

  {
    number: 10,
    title: 'Refunds',
    paragraphs: [
      "Where a refund is applicable, it will be processed according to the airline's fare rules and applicable MakeBeeTrip cancellation and refund conditions.",
      'The refundable amount may be reduced by applicable airline penalties, supplier charges, taxes and service charges.',
      "Once a refund has been processed, the time required for the amount to reflect in the customer's account may depend on the payment gateway, bank or card issuer.",
    ],
  },

  {
    number: 11,
    title: 'Non-Refundable Tickets',
    paragraphs: [
      'Certain flight fares may be marked as Non-Refundable or may contain non-refundable components.',
      "For such fares, the amount paid may not be refundable except where permitted under the airline's applicable rules or applicable law.",
      'Customers should carefully review the fare conditions before making payment.',
    ],
  },

  {
    number: 12,
    title: 'No-Show',
    paragraphs: [
      'If a passenger does not travel on the booked flight without cancelling or modifying the booking according to the applicable airline rules, the booking may be treated as a No-Show.',
      'No-show charges may apply.',
      "In some cases, a no-show may result in the loss of the ticket value or cancellation of subsequent sectors on the same booking, depending on the airline's rules.",
    ],
  },

  {
    number: 13,
    title: 'Connecting Flights',
    paragraphs: [
      'Passengers are responsible for checking the connection time and applicable transit requirements for their itinerary.',
      'Where flights are booked under separate tickets, the airlines or MakeBeeTrip may not be responsible for missed connections resulting from delays to another separately booked flight.',
      'Passengers should carefully review their complete itinerary before booking.',
    ],
  },

  {
    number: 14,
    title: 'Baggage',
    paragraphs: [
      'Baggage allowance varies depending on:',
    ],
    bullets: [
      'Airline',
      'Route',
      'Fare type',
      'Cabin class',
      'Passenger type',
    ],
    after: [
      "The baggage allowance applicable to the booking will be based on the airline's fare conditions.",
      'Additional baggage may be subject to separate charges imposed by the airline.',
      "Passengers are responsible for complying with the airline's baggage restrictions.",
    ],
  },

  {
    number: 15,
    title: 'Meals and Special Requests',
    paragraphs: [
      'Meal preferences, wheelchair assistance, special assistance, seat preferences and other special requests are subject to airline availability and confirmation.',
      'A request does not guarantee that the airline will provide the requested service.',
      'Additional charges may apply.',
    ],
  },

  {
    number: 16,
    title: 'Seat Selection',
    paragraphs: [
      'Seat selection may be subject to availability and additional airline charges.',
      'Some seats may be complimentary while others may require additional payment.',
      'Seat assignments may also be changed by the airline due to operational or safety reasons.',
    ],
  },

  {
    number: 17,
    title: 'Check-In',
    paragraphs: [
      'Passengers are responsible for completing the applicable airline check-in within the required time.',
      'Passengers should arrive at the airport sufficiently early to complete:',
    ],
    bullets: [
      'Check-in',
      'Baggage drop',
      'Security procedures',
      'Immigration formalities, where applicable',
      'Boarding procedures',
    ],
    after: [
      'Airlines may deny boarding if a passenger arrives after the applicable check-in or boarding deadline.',
    ],
  },

  {
    number: 18,
    title: 'Travel Documents',
    paragraphs: [
      'Passengers are responsible for carrying all required travel documents.',
      'MakeBeeTrip is not responsible for denied boarding or denied entry resulting from:',
    ],
    bullets: [
      'Invalid passport',
      'Expired passport',
      'Missing visa',
      'Incorrect visa',
      'Missing transit visa',
      'Incorrect passenger information',
      'Missing travel documents',
      'Failure to meet immigration requirements',
    ],
  },

  {
    number: 19,
    title: 'Special and Promotional Fares',
    paragraphs: [
      'Special, promotional, discounted or restricted fares may have additional conditions.',
      'Such fares may have:',
    ],
    bullets: [
      'Limited cancellation rights',
      'Limited amendment rights',
      'Non-refundable conditions',
      'Restricted baggage allowance',
      'Other airline-specific restrictions',
    ],
    after: [
      'Customers should review the applicable fare rules before completing payment.',
    ],
  },

  {
    number: 20,
    title: 'Payment Gateway',
    paragraphs: [
      'Flight payments may be processed through third-party payment gateway providers.',
      'Customers may be redirected to or connected with the secure payment environment of the applicable payment gateway.',
      'MakeBeeTrip may receive transaction-related information such as payment status, transaction ID and booking reference.',
      'Customers should never share their UPI PIN, card PIN, banking password or payment OTP with anyone.',
    ],
  },

  {
    number: 21,
    title: 'Customer Responsibility',
    paragraphs: [
      'Customers are responsible for checking the following immediately after receiving their booking confirmation:',
    ],
    bullets: [
      'Passenger names',
      'Flight number',
      'Travel date',
      'Departure airport',
      'Arrival airport',
      'Departure time',
      'Arrival time',
      'Baggage allowance',
      'Fare conditions',
      'Passport information, where applicable',
    ],
    after: [
      'Any error should be reported to MakeBeeTrip as soon as possible.',
    ],
  },

  {
    number: 22,
    title: 'Airline Terms Apply',
    paragraphs: [
      "Every flight booking is also subject to the applicable airline's terms and conditions and fare rules.",
      'Airline-specific rules may govern:',
    ],
    bullets: [
      'Cancellation',
      'Refund',
      'Rescheduling',
      'Baggage',
      'No-show',
      'Name correction',
      'Seat selection',
      'Check-in',
      'Schedule changes',
    ],
    after: [
      "Where applicable, the airline's fare rules will apply to the specific ticket.",
    ],
  },

  {
    number: 23,
    title: 'Limitation of Responsibility',
    paragraphs: [
      'MakeBeeTrip facilitates flight bookings but does not control airline operations.',
      'To the extent permitted by applicable law, MakeBeeTrip shall not be responsible for circumstances beyond its reasonable control, including airline delays, cancellations, schedule changes, operational disruptions, weather conditions, government restrictions or other unforeseen events.',
    ],
  },

  {
    number: 24,
    title: 'Changes to Flight Booking Terms',
    paragraphs: [
      'MakeBeeTrip may update these Flight Booking Terms & Conditions from time to time to reflect changes in our services, airline policies or applicable requirements.',
      'The updated version will be published on this page with a revised "Last Updated" date.',
    ],
  },
];

/* ============================================================
   Reusable section renderer
   ============================================================ */

function FlightTermsSection({ section }) {
  return (
    <section
      className="mbt-flight-legal-section"
      id={`flight-section-${section.number}`}
    >
      <div className="mbt-flight-section-heading">
        <span className="mbt-flight-section-number">
          {String(section.number).padStart(2, '0')}
        </span>

        <h2>{section.title}</h2>
      </div>

      {section.paragraphs?.map((paragraph, index) => (
        <p key={`paragraph-${section.number}-${index}`}>
          {paragraph}
        </p>
      ))}

      {section.emphasis && (
        <div className="mbt-flight-important-inline">
          {section.emphasis}
        </div>
      )}

      {section.bullets && (
        <ul className="mbt-flight-legal-list">
          {section.bullets.map((item, index) => (
            <li key={`bullet-${section.number}-${index}`}>
              {item}
            </li>
          ))}
        </ul>
      )}

      {section.after && (
        <>
          {Array.isArray(section.after) ? (
            section.after.map((paragraph, index) => (
              <p key={`after-${section.number}-${index}`}>
                {paragraph}
              </p>
            ))
          ) : (
            <p>{section.after}</p>
          )}
        </>
      )}
    </section>
  );
}

/* ============================================================
   Page
   ============================================================ */

export default function FlightTerms() {
  usePageMeta(
  "Flight Booking Terms & Conditions | MakeBeeTrip",
  "Terms specific to flight bookings on MakeBeeTrip: fares, schedules, baggage, airline policies, changes and cancellations.",
  "flight booking terms, airline terms and conditions, flight cancellation policy, baggage policy, fare rules"
);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight =
        document.documentElement.scrollHeight - window.innerHeight;

      setScrollProgress(
        totalHeight > 0
          ? Math.min((window.scrollY / totalHeight) * 100, 100)
          : 0
      );
    };

    window.addEventListener('scroll', handleScroll, {
      passive: true,
    });

    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <div className="mbt-flight-legal-wrapper">
      {/* Scroll progress */}
      <div
        className="mbt-flight-scroll-indicator"
        style={{ width: `${scrollProgress}%` }}
        aria-hidden="true"
      />

      {/* Sticky navigation */}
      <div className="mbt-flight-sticky-nav">
        <div className="mbt-flight-nav-container">
          <button
            type="button"
            className="mbt-flight-back-button"
            onClick={() => window.history.back()}
            aria-label="Go Back"
          >
            <svg
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>

            <span>Back</span>
          </button>
        </div>
      </div>

      {/* Hero */}
      <section className="mbt-flight-legal-hero">
        <div className="mbt-flight-hero-container">
          <div
            className="mbt-flight-legal-icon-box"
            aria-hidden="true"
          >
            <svg
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3.5 12.5l17-7-5.5 17-3.5-7-8-3z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M11.5 15.5l3.5-3.5"
              />
            </svg>
          </div>

          <span className="mbt-flight-hero-eyebrow">
            FLIGHT BOOKING TERMS
          </span>

          <h1>Flight Booking Terms &amp; Conditions</h1>

          <p className="mbt-flight-hero-desc">
            Please read these Flight Booking Terms &amp; Conditions carefully
            before searching, booking or purchasing a flight through
            MakeBeeTrip.
          </p>

          <div className="mbt-flight-updated-badge">
            Last Updated: 24 September 2026
          </div>
        </div>
      </section>

      {/* Main document */}
      <main className="mbt-flight-content-container">
        <article className="mbt-flight-legal-card">
          {/* Introduction */}
          <div className="mbt-flight-document-intro">
            <p>
              These Flight Booking Terms &amp; Conditions apply to all
              domestic and international flight bookings made through{' '}
              <strong>MakeBeeTrip Private Limited</strong>.
            </p>

            <p>
              By searching, booking or purchasing a flight through{' '}
              <a
                href="https://makebeetrip.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <strong>https://makebeetrip.com</strong>
              </a>
              , you acknowledge that you have read and agreed to these terms.
            </p>
          </div>

          {/* Quick Navigation */}
          <div
            className="mbt-flight-quick-nav"
            aria-label="Flight terms sections"
          >
            <div className="mbt-flight-quick-nav__eyebrow">
              QUICK NAVIGATION
            </div>

            <h2 className="mbt-flight-quick-nav__heading">
              Flight Booking Terms
            </h2>

            <div className="mbt-flight-quick-nav__links">
              {flightTerms.map((section) => (
                <a
                  key={section.number}
                  href={`#flight-section-${section.number}`}
                >
                  <span className="mbt-flight-quick-nav__number">
                    {String(section.number).padStart(2, '0')}
                  </span>

                  <span className="mbt-flight-quick-nav__title">
                    {section.title}
                  </span>

                  <span
                    className="mbt-flight-quick-nav__arrow"
                    aria-hidden="true"
                  >
                    →
                  </span>
                </a>
              ))}

              <a href="#flight-important-notice">
                <span className="mbt-flight-quick-nav__number">
                  !
                </span>

                <span className="mbt-flight-quick-nav__title">
                  Important Notice
                </span>

                <span
                  className="mbt-flight-quick-nav__arrow"
                  aria-hidden="true"
                >
                  →
                </span>
              </a>
            </div>
          </div>

          {/* Flight terms */}
          <div className="mbt-flight-document-section">
            {flightTerms.map((section, index) => (
              <React.Fragment key={section.number}>
                <FlightTermsSection section={section} />

                {index < flightTerms.length - 1 && (
                  <hr className="mbt-flight-divider" />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Important Notice */}
          <hr className="mbt-flight-divider" />

          <section
            id="flight-important-notice"
            className="mbt-flight-important-notice"
          >
            <div
              className="mbt-flight-important-notice__icon"
              aria-hidden="true"
            >
              !
            </div>

            <div>
              <span>IMPORTANT NOTICE</span>

              <p>
                All flight bookings are subject to the applicable airline&apos;s
                fare rules, terms and conditions, availability and operational
                policies. Customers are advised to carefully review the fare
                conditions and booking details before completing payment.
              </p>
            </div>
          </section>

          {/* Closing */}
          <div className="mbt-flight-legal-closing">
            MakeBeeTrip – Your Journey, Our Commitment.
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="mbt-flight-legal-footer">
        <div className="mbt-flight-footer-inner">
          <nav
            className="mbt-flight-footer-nav"
            aria-label="Legal navigation"
          >
            <a href="/terms">General Terms</a>
            <a href="/privacy">Privacy Policy</a>
            <a href="/payment-security">Payment Security</a>
            <a href="/cancellation-refund">
              Cancellation &amp; Refund
            </a>
            <a href="/disclaimer">Disclaimer</a>
            <a href="/hotel-terms">Hotel Terms</a>
            <a href="/holiday-terms">Holiday Terms</a>
            <a href="/visa-terms">Visa Terms</a>
            <a href="/about">About Us</a>
            <a href="/contact">Contact Us</a>
          </nav>

          <p className="mbt-flight-copyright">
            © 2026 MAKEBEETRIP PRIVATE LIMITED. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Back to top */}
      <button
        type="button"
        className="mbt-flight-scroll-top"
        onClick={scrollToTop}
        aria-label="Back to top"
      >
        ↑
      </button>
    </div>
  );
}